import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingContext, M_Fieldset, M_Button, BufferService } from 'src/M_Components'
import {
    Accordion, AccordionSummary, AccordionDetails, TextField, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton, Typography, Divider, Box, Tabs, Tab,
    List, ListItem, ListItemText, Checkbox, FormControlLabel, MenuItem, Grid, Paper, Tooltip
} from '@mui/material'
import {
    ExpandMore, Delete, Edit, Settings, Numbers, Abc, Refresh, Add, Save, Close
} from '@mui/icons-material'

const dynamicCounterService = new BufferService('DynamicCounterConfig')
const ENTITY_TYPES = ['Booking', 'BL', 'Quote']

export const DynamicCounterSettings = () => {
    const ctx = useContext(LoadingContext)
    const { t } = useTranslation()

    // Estados de Dados
    const [configs, setConfigs] = useState([])
    const [expandedId, setExpandedId] = useState(null)
    const [editDialog, setEditDialog] = useState({ open: false, config: null })
    const [createDialog, setCreateDialog] = useState({ open: false, entityType: '' })
    
    // Estados de UI Interna
    const [tabValue, setTabValue] = useState(0)
    const [selectedKey, setSelectedKey] = useState(null)
    const [selectedPrefix, setSelectedPrefix] = useState(null)

    useEffect(() => { loadConfigs() }, [])

    const loadConfigs = async () => {
        ctx.setIsLoad(true)
        try {
            const data = await dynamicCounterService.get()
            setConfigs(Array.isArray(data) ? data : (data?.Items || []))
        } catch (error) {
            ctx.toastMessages('error', '', t('ErrorLoadingData'))
        } finally { ctx.setIsLoad(false) }
    }

    const handleCreate = async () => {
        if (!createDialog.entityType) return;
        ctx.setIsLoad(true);
        try {
            const newConfig = {
                EntityType: createDialog.entityType,
                DbStatus: "E",
                KeyConfigs: [],
                PrefixConfigs: []
            };

            const response = await dynamicCounterService.post(newConfig);

            if (response && !response.name) {
                ctx.toastMessages('success', '', t('CreatedSuccessfully'));
                setCreateDialog({ open: false, entityType: '' });
                loadConfigs(); 
            }
        } catch (error) {
            ctx.toastMessages('error', '', t('ErrorCreating'));
        } finally {
            ctx.setIsLoad(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!editDialog.config) return;
        
        const hasInvalidAddition = (editDialog.config.KeyConfigs || []).some(k => {
            const additionValue = parseInt(k.Addition, 10);
            return isNaN(additionValue) || additionValue < 1;
        });

        if (hasInvalidAddition) {
            // Dispara o toast do vosso framework com a mensagem de erro
            ctx.toastMessages('error', '', t('AdditionMustBeGreaterOrEqualToOne'));
            return; // Aborta a execução e não faz o pedido HTTP
        }

        ctx.setIsLoad(true);
        try {
            const payload = {
                ...editDialog.config,
                DbStatus: editDialog.config.DbStatus || "E",
                
                // Tratamento das Keys
                KeyConfigs: (editDialog.config.KeyConfigs || []).map(k => ({
                    ...k,
                    DbStatus: "E",
                    // CRÍTICO: Forçar inteiros e booleanos puros para o C# aceitar
                    Addition: parseInt(k.Addition, 10) || 0,
                    Positions: parseInt(k.Positions, 10) || 0,
                    LastNumber: parseInt(k.LastNumber, 10) || 0,
                    FillEnabled: Boolean(k.FillEnabled),
                    LastKeyCounter: parseInt(k.LastKeyCounter, 10) || 0,
                    // Garante que se for string vazia, vai apenas um caracter ou null
                    FillChar: k.FillChar ? k.FillChar.toString().charAt(0) : ''
                })),

                // Tratamento dos Prefixos
                PrefixConfigs: (editDialog.config.PrefixConfigs || []).map(p => ({
                    ...p,
                    DbStatus: "E",
                    // CRÍTICO: Sincronizar com as novas colunas do C#
                    Order: parseInt(p.Order, 10) || 0,
                    FillPositions: parseInt(p.FillPositions, 10) || 0,
                    IsActive: Boolean(p.IsActive),
                    // Garante que se for string vazia, vai apenas um caracter ou null
                    FillChar: p.FillChar ? p.FillChar.toString().charAt(0) : ''
                }))
            };

            const response = await dynamicCounterService.update(payload.Id, payload);
            if (response && !response.name) {
                ctx.toastMessages('success', '', t('SavedSuccessfully'));
                setEditDialog({ open: false, config: null });
                loadConfigs();
            }
        } catch (error) {
            ctx.toastMessages('error', '', t('ErrorSaving'));
        } finally {
            ctx.setIsLoad(false);
        }
    };

    const updateKeyField = (field, value) => {
        setEditDialog(prev => {
            // 1. Mapeia a lista de Keys para encontrar a que está selecionada
            const newList = prev.config.KeyConfigs.map(k => 
                k.Id === selectedKey.Id ? { ...k, [field]: value } : k
            );
            // 2. Atualiza o objeto selecionado para o formulário do lado direito refletir a mudança
            const updatedItem = newList.find(i => i.Id === selectedKey.Id);
            setSelectedKey(updatedItem);
            
            // 3. Devolve o novo estado do diálogo
            return { ...prev, config: { ...prev.config, KeyConfigs: newList } };
        });
    };

    /*const handleAddPrefix = () => {
        const newPrefix = {
            // Gera um ID temporário único para o frontend
            Id: crypto.randomUUID ? crypto.randomUUID() : `temp-${Date.now()}`,
            DynamicCounterId: editDialog.config.Id,
            Prefix: '',
            Separator: '',
            IsActive: true,
            Comments: '',
            DbStatus: 'E' // Mantendo o padrão "E" que descobrimos no backend
        };

        setEditDialog(prev => ({
            ...prev,
            config: {
                ...prev.config,
                PrefixConfigs: [...(prev.config.PrefixConfigs || []), newPrefix]
            }
        }));

        // Seleciona o novo prefixo para edição imediata
        setSelectedPrefix(newPrefix);
    };*/
    const handleAddPrefix = () => {
        // Validação: Não permite criar prefixo sem uma chave selecionada
        if (!selectedKey) {
            ctx.toastMessages('error', '', t('SelectAKeyFirst'));
            return;
        }

        const newPrefix = {
            Id: crypto.randomUUID ? crypto.randomUUID() : `temp-${Date.now()}`,
            DynamicCounterId: editDialog.config.Id,
            Prefix: '',
            Separator: '',
            IsActive: true,
            Comments: '',
            DbStatus: 'E',
            Order: (selectedKey.PrefixConfigs?.length || 0) + 1
        };

        setEditDialog(prev => {
            const updatedKeyConfigs = prev.config.KeyConfigs.map(k => {
                if (k.Id === selectedKey.Id) {
                    return { ...k, PrefixConfigs: [...(k.PrefixConfigs || []), newPrefix] };
                }
                return k;
            });
            return { ...prev, config: { ...prev.config, KeyConfigs: updatedKeyConfigs } };
        });

        // Atualiza a visualização local para incluir o novo item na lista da esquerda
        setSelectedKey(prev => ({ ...prev, PrefixConfigs: [...(prev.PrefixConfigs || []), newPrefix] }));
        setSelectedPrefix(newPrefix);
    };

    const handleAddKey = () => {
        // Criamos um novo objeto com os valores padrão (default)
        const newKey = {
            Id: crypto.randomUUID(), // Guid vazio ou null (o backend gera o real)
            DynamicCounterId: editDialog.config.Id,
            Key1Value: '',
            Key1Option: 'Blank',
            Key2Value: '',
            Key2Option: 'Blank',
            Key3Value: '',
            Key3Option: 'Blank',
            Key4Value: '',
            Key4Option: 'Blank',
            Key5Value: '',
            Key5Option: 'Blank',
            Addition: 1,
            IsReady: false,
            LastNumber: 0,
            Positions: 0,
            FillEnabled: false,
            FillChar: '',
            Comments: ''
        };

        setEditDialog(prev => ({
            ...prev,
            config: {
                ...prev.config,
                KeyConfigs: [...(prev.config.KeyConfigs || []), newKey]
            }
        }));

        // Selecionamos logo a nova key para edição
        setSelectedKey(newKey);
    };

    /*const updatePrefixField = (field, value) => {
        setEditDialog(prev => {
            const newList = prev.config.PrefixConfigs.map(p => 
                p.Id === selectedPrefix.Id ? { ...p, [field]: value } : p
            );
            const updatedItem = newList.find(i => i.Id === selectedPrefix.Id);
            setSelectedPrefix(updatedItem);
            return { ...prev, config: { ...prev.config, PrefixConfigs: newList } };
        });
    };*/
    const updatePrefixField = (field, value) => {
        setEditDialog(prev => {
            // Percorre as chaves para achar a que está selecionada
            const updatedKeyConfigs = prev.config.KeyConfigs.map(k => {
                if (k.Id === selectedKey.Id) {
                    // Atualiza o prefixo correto dentro desta chave
                    const updatedPrefixes = k.PrefixConfigs.map(p => 
                        p.Id === selectedPrefix.Id ? { ...p, [field]: value } : p
                    );
                    
                    // Sincroniza o estado do formulário do prefixo do lado direito
                    const updatedItem = updatedPrefixes.find(i => i.Id === selectedPrefix.Id);
                    setSelectedPrefix(updatedItem);
                    
                    return { ...k, PrefixConfigs: updatedPrefixes };
                }
                return k;
            });

            // Sincroniza o estado da chave selecionada na UI para carregar a nova lista de filhos
            const currentKey = updatedKeyConfigs.find(i => i.Id === selectedKey.Id);
            setSelectedKey(currentKey);

            return { ...prev, config: { ...prev.config, KeyConfigs: updatedKeyConfigs } };
        });
    };

    const handleDeleteKey = (keyId, event) => {
        event.stopPropagation(); // Evita que selecione a linha ao clicar no botão
        
        setEditDialog(prev => {
            const newList = (prev.config.KeyConfigs || []).filter(k => k.Id !== keyId);
            
            // Se a key apagada for a que estava selecionada, limpamos a seleção
            if (selectedKey?.Id === keyId) {
                setSelectedKey(newList.length > 0 ? newList[0] : null);
            }
            
            return {
                ...prev,
                config: { ...prev.config, KeyConfigs: newList }
            };
        });
    };

    /*const handleDeletePrefix = (prefixId, event) => {
        event.stopPropagation(); // Evita selecionar a linha
        
        setEditDialog(prev => {
            const newList = (prev.config.PrefixConfigs || []).filter(p => p.Id !== prefixId);
            
            if (selectedPrefix?.Id === prefixId) {
                setSelectedPrefix(newList.length > 0 ? newList[0] : null);
            }
            
            return {
                ...prev,
                config: { ...prev.config, PrefixConfigs: newList }
            };
        });
    };*/
    const handleDeletePrefix = (prefixId, event) => {
        event.stopPropagation();
        
        setEditDialog(prev => {
            const updatedKeyConfigs = prev.config.KeyConfigs.map(k => {
                if (k.Id === selectedKey.Id) {
                    const newList = (k.PrefixConfigs || []).filter(p => p.Id !== prefixId);
                    if (selectedPrefix?.Id === prefixId) {
                        setSelectedPrefix(newList.length > 0 ? newList[0] : null);
                    }
                    return { ...k, PrefixConfigs: newList };
                }
                return k;
            });
            return { ...prev, config: { ...prev.config, KeyConfigs: updatedKeyConfigs } };
        });

        // Sincroniza a chave selecionada localmente
        setSelectedKey(prev => ({ ...prev, PrefixConfigs: (prev.PrefixConfigs || []).filter(p => p.Id !== prefixId) }));
    };

    const handleCloseEditDialog = () => {
        setEditDialog({ open: false, config: null });
        setSelectedKey(null);
        setSelectedPrefix(null);
        setTabValue(0);
    };

        // Função para tratar a mudança na Combo de Reference
    const handleReferenceChange = (newValue) => {
        setEditDialog(prev => {
            const updatedKeyConfigs = prev.config.KeyConfigs.map(k => {
                if (k.Id === selectedKey.Id) {
                    const updatedPrefixes = k.PrefixConfigs.map(p => {
                        if (p.Id === selectedPrefix.Id) {
                            // Se escolheu algo diferente de 'Blank', limpa obrigatoriamente o Prefix Tag
                            return { 
                                ...p, 
                                Reference: newValue, 
                                Prefix: newValue !== 'Blank' ? '' : p.Prefix 
                            };
                        }
                        return p;
                    });
                    setSelectedPrefix(updatedPrefixes.find(i => i.Id === selectedPrefix.Id));
                    return { ...k, PrefixConfigs: updatedPrefixes };
                }
                return k;
            });
            const currentKey = updatedKeyConfigs.find(i => i.Id === selectedKey.Id);
            setSelectedKey(currentKey);
            return { ...prev, config: { ...prev.config, KeyConfigs: updatedKeyConfigs } };
        });
    };

    const handlePrefixTagChange = (newValue) => {
        setEditDialog(prev => {
            const updatedKeyConfigs = prev.config.KeyConfigs.map(k => {
                if (k.Id === selectedKey.Id) {
                    const updatedPrefixes = k.PrefixConfigs.map(p => {
                        if (p.Id === selectedPrefix.Id) {
                            // Se escreveu texto, a Reference passa forçadamente para 'Blank'
                            return { 
                                ...p, 
                                Prefix: newValue, 
                                Reference: newValue.trim() !== '' ? 'Blank' : p.Reference 
                            };
                        }
                        return p;
                    });
                    setSelectedPrefix(updatedPrefixes.find(i => i.Id === selectedPrefix.Id));
                    return { ...k, PrefixConfigs: updatedPrefixes };
                }
                return k;
            });
            const currentKey = updatedKeyConfigs.find(i => i.Id === selectedKey.Id);
            setSelectedKey(currentKey);
            return { ...prev, config: { ...prev.config, KeyConfigs: updatedKeyConfigs } };
        });
    };



    const renderKeysTab = () => {
        if (!editDialog.config) return null;

        return (
            <Grid container sx={{ height: '520px' }}>
                {/* LADO ESQUERDO: LISTA EM ESTILO GRELHA (Igual aos Prefixos) */}
                <Grid item xs={5} sx={{ borderRight: '1px solid #eee', p: 1, bgcolor: '#fcfcfc' }}>
                    
                    
                    <Paper variant="outlined">
                        <List dense sx={{ maxHeight: '420px', overflow: 'auto', p: 0 }}>
                            {/* Cabeçalho Fixo da Tabela */}
                            <ListItem sx={{ bgcolor: '#eee', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, zIndex: 1 }}>
                                <Grid container spacing={1}>
                                    <Grid item xs={3.5}><Typography variant="caption" fontWeight="bold">Key 1</Typography></Grid>
                                    <Grid item xs={3.5}><Typography variant="caption" fontWeight="bold">Key 2</Typography></Grid>
                                    <Grid item xs={3.5}><Typography variant="caption" fontWeight="bold">Key 3</Typography></Grid>
                                    <Grid item xs={1.5} sx={{ display: 'flex', justifyContent: 'flex-end' }}></Grid>
                                </Grid>
                            </ListItem>
                            
                            {/* Linhas de Dados */}
                            {editDialog.config?.KeyConfigs?.map((key, idx) => (
                                <ListItem 
                                    button 
                                    key={key.Id || idx} 
                                    selected={selectedKey?.Id === key.Id} 
                                    onClick={() => setSelectedKey(key)}
                                    sx={{ borderBottom: '1px solid #f0f0f0' }}
                                >
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item xs={3.5}>
                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {key.Key1Value || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3.5}>
                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {key.Key2Value || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3.5}>
                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {key.Key3Value || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1.5} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton 
                                                size="small" 
                                                color="error" 
                                                onClick={(e) => handleDeleteKey(key.Id, e)}
                                                sx={{ '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.08)' } }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                    <Box sx={{ mb: 1, display: 'flex', gap: 1 }}>
                        <IconButton size="small" onClick={handleAddKey} title={t('New')}>
                            <Add fontSize="small" />
                        </IconButton>
                    </Box>
                </Grid>

                {/* LADO DIREITO: FORMULÁRIO DE DETALHES (Mantém todos os campos originais) */}
                <Grid item xs={7} sx={{ pl: 3, pr: 1, overflowY: 'auto', pt: 1 }}>
                    {selectedKey ? (
                        <Grid container spacing={2}>
                            {[1, 2, 3, 4, 5].map(n => (
                                <React.Fragment key={n}>
                                    <Grid item xs={7}>
                                        <TextField 
                                            label={`Key ${n} Value`} 
                                            size="small" 
                                            fullWidth 
                                            value={selectedKey[`Key${n}Value`] || ''} 
                                            onChange={(e) => updateKeyField(`Key${n}Value`, e.target.value)} 
                                        />
                                    </Grid>
                                    <Grid item xs={5}>
                                        <TextField 
                                            select 
                                            label="Option" 
                                            size="small" 
                                            fullWidth 
                                            value={selectedKey[`Key${n}Option`] || 'LineCode'} 
                                            onChange={(e) => updateKeyField(`Key${n}Option`, e.target.value)}
                                        >
                                            <MenuItem value="LineCode">LineCode</MenuItem>
                                            <MenuItem value="Year">Year</MenuItem>
                                            <MenuItem value="Loading">Loading</MenuItem>
                                            <MenuItem value="Discharge">Discharge</MenuItem>
                                        </TextField>
                                    </Grid>
                                </React.Fragment>
                            ))}
                            
                            <Grid item xs={4}><TextField label="Addition" type="number" size="small" fullWidth value={selectedKey.Addition || 0} onChange={(e) => updateKeyField('Addition', e.target.value)} /></Grid>
                            <Grid item xs={4}><TextField 
                                label={t('LastKeyCounter')} 
                                type="number" 
                                size="small" 
                                fullWidth 
                                value={selectedKey.LastKeyCounter || 0}
                                // O readOnly foi removido e adicionámos o onChange
                                onChange={(e) => updateKeyField('LastKeyCounter', e.target.value)}
                            /></Grid>
                            <Grid item xs={4}><TextField label="Positions" type="number" size="small" fullWidth value={selectedKey.Positions || 0} onChange={(e) => updateKeyField('Positions', e.target.value)} /></Grid>
                            <Grid item xs={4}><TextField label="Fill Char" size="small" fullWidth value={selectedKey.FillChar || ''} onChange={(e) => updateKeyField('FillChar', e.target.value)} /></Grid>
                            <Grid item xs={6}><FormControlLabel control={<Checkbox checked={selectedKey.FillEnabled || false} onChange={(e) => updateKeyField('FillEnabled', e.target.checked)} />} label="Fill Enabled" /></Grid>
                            <Grid item xs={6}><FormControlLabel control={<Checkbox checked={selectedKey.IsReady || false} onChange={(e) => updateKeyField('IsReady', e.target.checked)} />} label="Is Ready" /></Grid>
                            <Grid item xs={12}><TextField label="Comments" multiline rows={2} fullWidth value={selectedKey.Comments || ''} onChange={(e) => updateKeyField('Comments', e.target.value)} /></Grid>
                        </Grid>
                    ) : (
                        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography color="textSecondary">{t('SelectAKeyToEdit')}</Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>
        );
    };

        const renderPrefixTab = () => {
        // 1. Guarda de segurança: Se o diálogo estiver a fechar, não renderiza nada
        if (!editDialog.config) return null;

        // 2. Proteção de Hierarquia: Se o utilizador não selecionou nenhuma Chave na primeira aba,
        // exibe uma mensagem amigável em vez de deixar os campos vazios ou dar erro.
        if (!selectedKey) {
            return (
                <Box sx={{ display: 'flex', height: '400px', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                    <Typography color="textSecondary" variant="body1" textAlign="center">
                        {t('SelectAKeyInTheFirstTabToManageItsPrefixes')}
                    </Typography>
                </Box>
            );
        }

        return (
            <Grid container sx={{ height: '520px' }}>
                {/* LADO ESQUERDO: LISTA DE PREFIXOS DA CHAVE SELECIONADA */}
                <Grid item xs={5} sx={{ borderRight: '1px solid #eee', p: 1, bgcolor: '#fcfcfc' }}>
                    
                    
                    <Paper variant="outlined">
                        <List dense sx={{ maxHeight: '420px', overflow: 'auto', p: 0 }}>
                            <ListItem sx={{ bgcolor: '#eee', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, zIndex: 1 }}>
                                <Grid container>
                                    <Grid item xs={3}><Typography variant="caption" fontWeight="bold">Order</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="caption" fontWeight="bold">Prefix Tag</Typography></Grid>
                                    <Grid item xs={3}><Typography variant="caption" fontWeight="bold">Fill?</Typography></Grid>
                                </Grid>
                            </ListItem>
                            
                            {/* Lemos os prefixos diretamente da Chave selecionada (selectedKey) */}
                            {selectedKey.PrefixConfigs?.map((p, idx) => (
                                <ListItem 
                                    button 
                                    key={p.Id || idx} 
                                    selected={selectedPrefix?.Id === p.Id} 
                                    onClick={() => setSelectedPrefix(p)}
                                    sx={{ borderBottom: '1px solid #f0f0f0' }}
                                >
                                    <Grid container alignItems="center">
                                        <Grid item xs={2}><Typography variant="body2">{p.Order || idx + 1}</Typography></Grid>
                                        <Grid item xs={5}>
                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {p.Prefix || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}><Checkbox checked={p.IsActive || false} size="small" disabled /></Grid>
                                        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton 
                                                size="small" 
                                                color="error" 
                                                onClick={(e) => handleDeletePrefix(p.Id, e)}
                                                sx={{ '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.08)' } }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                    <Box sx={{ mb: 1, display: 'flex', gap: 1 }}>
                        <IconButton size="small" onClick={handleAddPrefix} title={t('New')}>
                            <Add fontSize="small" />
                        </IconButton>
                    </Box>
                </Grid>

                {/* LADO DIREITO: FORMULÁRIO DE EDIÇÃO DO PREFIXO */}
                <Grid item xs={7} sx={{ pl: 3, pt: 1 }}>
                    {selectedPrefix ? (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField 
                                    label="Order" 
                                    size="small" 
                                    fullWidth 
                                    type="number" 
                                    value={selectedPrefix.Order || ''} 
                                    onChange={(e) => updatePrefixField('Order', e.target.value)}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField 
                                    label="Prefix Tag" 
                                    size="small" 
                                    fullWidth 
                                    value={selectedPrefix.Prefix || ''} 
                                    // SÓ BLOQUEIA se a Reference tiver um valor selecionado que NÃO SEJA 'Blank'
                                    disabled={!!selectedPrefix.Reference && selectedPrefix.Reference !== 'Blank'}
                                    onChange={(e) => handlePrefixTagChange(e.target.value)}
                                />
                            </Grid>

                            {/* Combo de Reference */}
                            <Grid item xs={12}>
                                <TextField 
                                    select 
                                    label="Reference" 
                                    size="small" 
                                    fullWidth 
                                    value={selectedPrefix.Reference || 'Blank'} 
                                    // SÓ BLOQUEIA se o Prefix Tag tiver texto real escrito
                                    disabled={!!selectedPrefix.Prefix && selectedPrefix.Prefix.trim() !== ''}
                                    onChange={(e) => handleReferenceChange(e.target.value)}
                                >
                                    <MenuItem value="Blank">Blank</MenuItem>
                                    <MenuItem value="LineCode">LineCode</MenuItem>
                                    <MenuItem value="Year">Year</MenuItem>
                                    <MenuItem value="Loading">Loading</MenuItem>
                                    <MenuItem value="Discharge">Discharge</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={6}>
                                <TextField 
                                    label="Fill Char" 
                                    size="small" 
                                    fullWidth 
                                    value={selectedPrefix.Separator || ''} 
                                    onChange={(e) => updatePrefixField('Separator', e.target.value)} 
                                />
                            </Grid>

                            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField 
                                    label="Fill Positions" 
                                    size="small" 
                                    type="number" 
                                    sx={{ width: '100px' }}
                                    value={selectedPrefix.FillPositions || 0} 
                                    onChange={(e) => updatePrefixField('FillPositions', e.target.value)} 
                                />
                                <FormControlLabel 
                                    control={
                                        <Checkbox 
                                            size="small" 
                                            checked={selectedPrefix.IsActive || false} 
                                            onChange={(e) => updatePrefixField('IsActive', e.target.checked)} 
                                        />
                                    } 
                                    label="Fill?" 
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField 
                                    select 
                                    label="Cut Type" 
                                    size="small" 
                                    fullWidth 
                                    value={selectedPrefix.CutType || 'Right'} 
                                    onChange={(e) => updatePrefixField('CutType', e.target.value)}
                                >
                                    <MenuItem value="Right">Right</MenuItem>
                                    <MenuItem value="Left">Left</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    ) : (
                        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography color="textSecondary">{t('SelectAPrefixToEdit')}</Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>
        );
    };


    return (
        <Box sx={{ p: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('DynamicCounterSettings')}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <M_Button icon={<Refresh />} label={t('Refresh')} onClick={loadConfigs} normal />
                    <M_Button icon={<Add />} label={t('New')} onClick={() => setCreateDialog({ open: true, entityType: '' })} backgroundColor="var(--color-green)" labelColor="white" />
                </Box>
            </Box>
            
            <M_Fieldset label={t('Configurations')} elements={
                <Box sx={{ mt: 1 }}>
                    {configs.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc' }}>{t('NoDataFound')}</Paper>
                    ) : (
                        configs.map(config => (
                            <Accordion key={config.Id} expanded={expandedId === config.Id} onChange={() => setExpandedId(expandedId === config.Id ? null : config.Id)} sx={{ mb: 1, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Settings sx={{ mr: 2, color: 'var(--color-blue)' }} />
                                        <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{config.EntityType}</Typography>
                                        <IconButton 
                                            size="small" 
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                
                                                // 1. Reset completo às seleções das abas internas
                                                setSelectedKey(null);
                                                setSelectedPrefix(null);
                                                setTabValue(0); // Força voltar sempre para a primeira Tab (Keys)
                                                
                                                // 2. Só depois abre o diálogo com a nova configuração pai
                                                setEditDialog({ open: true, config: config }); 
                                            }}
                                        >
                                            <Edit fontSize="small" color="primary" />
                                        </IconButton>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ bgcolor: '#fafafa' }}>
                                    <Typography variant="caption">Keys: {config.KeyConfigs?.length} | Prefixes: {config.PrefixConfigs?.length}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))
                    )}
                </Box>
            }/>

            {/* Dialog Edit */}
            <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, config: null })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#f8f9fa', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">{editDialog.config?.EntityType}</Typography>
                    <IconButton onClick={() => setEditDialog({ open: false, config: null })}><Close /></IconButton>
                </DialogTitle>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} centered sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab icon={<Numbers />} label="Keys" />
                    <Tab icon={<Abc />} label="Prefixes" />
                </Tabs>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2 }}>{tabValue === 0 ? renderKeysTab() : renderPrefixTab()}</Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                    <M_Button label={t('Cancel')} onClick={handleCloseEditDialog} />
                    <M_Button icon={<Save />} label={t('Save')} onClick={handleSaveEdit} backgroundColor="var(--color-blue)" labelColor="white" />
                </DialogActions>
            </Dialog>

             {/* Dialog Create - Corrigido */}
            {/* NOVO DIALOG DE CRIAÇÃO - ESTRUTURA SIMPLIFICADA */}
            {/* NOVO DIALOG DE CRIAÇÃO - ESTRUTURA SIMPLIFICADA */}
            <Dialog 
                open={createDialog.open} 
                onClose={() => setCreateDialog({ open: false, entityType: '' })}
            >
                <DialogTitle>{t('NewConfiguration')}</DialogTitle>
                
                <DialogContent sx={{ pt: 2, minWidth: '300px' }}>
                    <div style={{ marginTop: '10px' }}>
                        <Typography variant="caption">{t('EntityType')}</Typography>

                        <select
                            style={{ width: '100%', padding: '10px', marginTop: '5px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }}
                            value={createDialog.entityType}
                            onChange={(e) =>
                                setCreateDialog({
                                    ...createDialog,
                                    entityType: e.target.value
                                })
                            }
                        >
                            <option value="">{t('Select')}</option>
                            {ENTITY_TYPES.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <M_Button 
                        label={t('Cancel')} 
                        onClick={() => setCreateDialog({ open: false, entityType: '' })} 
                    />
                    <M_Button 
                        label={t('Create')} 
                        onClick={handleCreate} 
                        backgroundColor="var(--color-blue)" 
                        labelColor="white" 
                    />
                </DialogActions>
            </Dialog>
        </Box> // Fecho do componente principal
    )
}

export default DynamicCounterSettings